import React from 'react'
import PropTypes from 'prop-types'
import {Tag, Tooltip} from 'antd'
import {
    COUNT_UNIT_LABELS,
    DETECT_FIELD_LABELS,
    PACING_LABELS,
    STAGE_TYPE_LABELS,
    TIME_BASIS_LABELS,
} from '../../constants'

/**
 * Nhãn màu cho thuộc tính công đoạn.
 *
 * ⚠️ Đơn vị đếm sản lượng và Cách tính giờ là hai trường **độc lập tuyệt đối** (D-CR-17, RB-12) và
 *    **cấm gộp thành một cột** (UI-02b). Gộp lại chính là nguồn gốc của hiểu nhầm "sản lượng của
 *    In, Cắt, Khắc đếm theo dòng vật liệu". Vì vậy đây là hai component riêng, không phải một.
 *
 * ⚠️ Nhãn luôn hiển thị đầy đủ chữ tiếng Việt, không viết tắt, không thay bằng ký hiệu (M18 §A.4.2).
 *    Màn hình tại xưởng chất lượng thấp và một phần người dùng khó phân biệt màu, nên màu chỉ là
 *    kênh nhận biết phụ.
 */

export function StageTypeTag({stageType}) {
    const label = STAGE_TYPE_LABELS[stageType]

    if (!label) return null

    return <Tag color={label.color || undefined}>{label.text}</Tag>
}

StageTypeTag.propTypes = {stageType: PropTypes.string.isRequired}

export function PacingTag({pacing}) {
    const label = PACING_LABELS[pacing]

    if (!label) return null

    return (
        <Tooltip title="Tốc độ công đoạn do máy quyết định hay do tay người quyết định">
            <Tag color={label.color}>{label.text}</Tag>
        </Tooltip>
    )
}

PacingTag.propTypes = {pacing: PropTypes.string.isRequired}

/** Đơn vị đếm sản lượng — con số hiển thị ở cột Sản lượng của báo cáo (D-CR-17). */
export function CountUnitTag({countUnit, variant}) {
    const label = COUNT_UNIT_LABELS[countUnit]

    if (!label) return null

    return (
        <Tooltip title="Con số ở cột Sản lượng được đếm theo đơn vị này. Với In, Cắt, Khắc thì đếm theo item, KHÔNG đếm theo dòng nguyên vật liệu">
            <Tag color={label.color}>{variant === 'm18' ? label.m18 : label.m16}</Tag>
        </Tooltip>
    )
}

CountUnitTag.propTypes = {
    countUnit: PropTypes.string.isRequired,
    variant: PropTypes.oneOf(['m16', 'm18']),
}

/**
 * Cách tính giờ, kèm tên đại lượng khi tính theo dữ liệu file in (B-AC-02d).
 *
 * ⚠️ `stacked` xếp thẻ TRÊN, tên đại lượng DƯỚI — và đó là lựa chọn về BỀ RỘNG, không phải về thẩm
 *    mỹ. Để cặp `[Theo dữ liệu detect] cutting_length` nằm cùng một dòng thì cột phải **245px**
 *    (thẻ 119 + khe 4 + chip 101 + 16 padding), trong khi cột chỉ cần **140px** là đủ cho riêng
 *    cái thẻ. Không bật `stacked` thì `flex-wrap` vẫn đẩy chip xuống dòng ở mọi bề rộng dưới 245 —
 *    tức vẫn hai dòng, nhưng cao thấp so le giữa `area` (29px, có cột vừa) và `cutting_length`
 *    (101px, không cột nào vừa). `stacked` biến cái so le đó thành một dạng duy nhất.
 */
export function TimeBasisTag({timeBasis, detectField, variant, stacked}) {
    const label = TIME_BASIS_LABELS[timeBasis]

    if (!label) return null

    return (
        <Tooltip title="Cách ra con số giờ, độc lập với đơn vị đếm. Theo item/đơn = định mức nhân 1. Theo dữ liệu detect = cộng theo từng dòng nguyên vật liệu của item">
            <span className={stacked ? 'VcTimeBasis VcTimeBasis--stacked' : 'VcTimeBasis'}>
                <Tag color={label.color || undefined}>{variant === 'm18' ? label.m18 : label.m16}</Tag>
                {timeBasis === 'by_detect' && detectField && (
                    <code className="VcDetectField">{DETECT_FIELD_LABELS[detectField]}</code>
                )}
            </span>
        </Tooltip>
    )
}

TimeBasisTag.propTypes = {
    timeBasis: PropTypes.string.isRequired,
    detectField: PropTypes.string,
    variant: PropTypes.oneOf(['m16', 'm18']),
    stacked: PropTypes.bool,
}
